import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { pinnedChartsApi } from '../services/api';

const PinnedChartsContext = createContext();

export function PinnedChartsProvider({ children }) {
  const [pinnedCharts, setPinnedCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshingChartId, setRefreshingChartId] = useState(null);
  const [pinningInProgress, setPinningInProgress] = useState(false);

  // Fetch all pinned charts for the user
  const fetchPinnedCharts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await pinnedChartsApi.getPinnedCharts();
      setPinnedCharts(response.charts || []);
    } catch (err) {
      console.error('Failed to fetch pinned charts:', err);
      setError(err.message);
      setPinnedCharts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Pin a new chart
  const pinChart = useCallback(async (chartData) => {
    try {
      setPinningInProgress(true);
      setError(null);

      const newChart = await pinnedChartsApi.pinChart(chartData);

      // Add to local state
      setPinnedCharts(prev => [...prev, newChart]);

      return { success: true, chart: newChart };
    } catch (err) {
      console.error('Failed to pin chart:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setPinningInProgress(false);
    }
  }, []);

  // Unpin a chart
  const unpinChart = useCallback(async (chartId) => {
    try {
      setError(null);
      await pinnedChartsApi.unpinChart(chartId);

      // Remove from local state
      setPinnedCharts(prev => prev.filter(c => c.id !== chartId));

      return { success: true };
    } catch (err) {
      console.error('Failed to unpin chart:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  // Refresh a chart's data (re-execute SQL)
  const refreshChart = useCallback(async (chartId) => {
    try {
      setRefreshingChartId(chartId);
      setError(null);

      const result = await pinnedChartsApi.refreshChart(chartId);

      if (result.success && result.chart) {
        // Update local state with refreshed chart
        setPinnedCharts(prev =>
          prev.map(c => c.id === chartId ? result.chart : c)
        );
      }

      return result;
    } catch (err) {
      console.error('Failed to refresh chart:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setRefreshingChartId(null);
    }
  }, []);

  // Update a chart (title, order)
  const updateChart = useCallback(async (chartId, updates) => {
    try {
      setError(null);
      const updatedChart = await pinnedChartsApi.updatePinnedChart(chartId, updates);

      // Update local state
      setPinnedCharts(prev =>
        prev.map(c => c.id === chartId ? updatedChart : c)
      );

      return { success: true, chart: updatedChart };
    } catch (err) {
      console.error('Failed to update chart:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  // Reorder charts
  const reorderCharts = useCallback(async (chartIds) => {
    try {
      setError(null);
      const result = await pinnedChartsApi.reorderCharts(chartIds);

      // Update local state with new order
      if (result.charts) {
        setPinnedCharts(result.charts);
      }

      return { success: true };
    } catch (err) {
      console.error('Failed to reorder charts:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  // Check if a chart with the same SQL query is already pinned
  // Using SQL as unique identifier since frontend message IDs aren't DB UUIDs
  const isChartPinnedBySql = useCallback((sqlQuery) => {
    if (!sqlQuery) return false;
    return pinnedCharts.some(c => c.sql_query === sqlQuery);
  }, [pinnedCharts]);

  // Fetch pinned charts on mount
  useEffect(() => {
    fetchPinnedCharts();
  }, [fetchPinnedCharts]);

  const value = {
    pinnedCharts,
    loading,
    error,
    refreshingChartId,
    pinningInProgress,
    // Actions
    fetchPinnedCharts,
    pinChart,
    unpinChart,
    refreshChart,
    updateChart,
    reorderCharts,
    isChartPinnedBySql
  };

  return (
    <PinnedChartsContext.Provider value={value}>
      {children}
    </PinnedChartsContext.Provider>
  );
}

export function usePinnedCharts() {
  const context = useContext(PinnedChartsContext);
  if (!context) {
    throw new Error('usePinnedCharts must be used within a PinnedChartsProvider');
  }
  return context;
}

export default usePinnedCharts;
